// CARO. Casa de estilo — COLORIMETRY ME!: the slug of each season page per language
// (/estudio-de-estilo/colorimetria/otono-calido, /en/style-studio/colour-analysis/warm-autumn,
// /fr/studio-de-style/colorimetrie/automne-chaud). The Spanish slug is the logical one (code, tests,
// og cards); EN/FR come from the season names in seasons.json (`en`, and `ficheFr`, CARO's fiche
// names). Pure and small on purpose: the URL table (lib/seo/routes.ts) imports it into every bundle.

import seasonsJson from '@/content/colorimetry/seasons.json';
import type { Locale } from '@/types';
import { COLORIMETRY_PATH } from './route';

/** «Otoño cálido» → «otono-calido». */
export function slugify(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

interface SeasonNames {
  id: string;
  es: string;
  en: string;
  ficheFr: string;
}

/** Each season's slug in every language (`es` = the logical slug). */
export const SEASON_SLUGS: readonly ({ id: string } & Record<Locale, string>)[] = (
  seasonsJson as { seasons: SeasonNames[] }
).seasons.map((s) => ({ id: s.id, es: slugify(s.es), en: slugify(s.en), fr: slugify(s.ficheFr) }));

/** Public slug of the season whose Spanish slug is `esSlug`, in `locale` (unknown: unchanged). */
export function seasonSlugFor(locale: Locale, esSlug: string): string {
  return SEASON_SLUGS.find((s) => s.es === esSlug)?.[locale] ?? esSlug;
}

/**
 * Spanish (logical) slug of a public season slug. The slug of the locale wins; the Spanish slug
 * and another language's slug are accepted too, so old or mixed URLs resolve (and get their 301).
 * Unknown: unchanged (the page then 404s).
 */
export function seasonEsSlug(locale: Locale, slug: string): string {
  const own = SEASON_SLUGS.find((s) => s[locale] === slug);
  if (own) return own.es;
  return SEASON_SLUGS.find((s) => s.es === slug || s.en === slug || s.fr === slug)?.es ?? slug;
}

/** Logical path of a season page (add the language with localizedPath); light, for the landings too. */
export function seasonPath(seasonId: string): string {
  const s = SEASON_SLUGS.find((x) => x.id === seasonId);
  if (!s) throw new Error(`slugs: estación desconocida ${seasonId}`);
  return `${COLORIMETRY_PATH}/${s.es}`;
}

/**
 * A season's anchor in the guide «Combinar colores» (GUIDE_PATH, lib/studio/guide.ts): its slug in that
 * language (#otono-calido, #warm-autumn, #automne-chaud). Here, light, so the season pages can link
 * `GUIDE_PATH#anchor` without loading the guide's texts.
 */
export function guideAnchor(locale: Locale, seasonId: string): string {
  const s = SEASON_SLUGS.find((x) => x.id === seasonId);
  if (!s) throw new Error(`slugs: estación desconocida ${seasonId}`);
  return s[locale];
}
